import React, { useState, useEffect, useRef, useMemo } from 'react';
import baseapi from '../../../services/baseapi';
import { X, Search, RefreshCw, Copy, Check, AlertCircle, FileText, AlignLeft } from 'lucide-react';

interface LogEntry {
    timestamp: string;
    container: string;
    message: string;
    level: string;
    reason?: string;
    restarts?: number;
}

interface LogsResponse {
    status: boolean;
    count: number;
    pod_uid: string;
    pod_name: string;
    namespace: string;
    container: string | null;
    data: LogEntry[];
}

interface K8sPodLogsModalProps {
    agentId: string;
    clientId: string;
    podUid: string;
    podName: string;
    initialContainer?: string;
    onClose: () => void;
}

export const K8sPodLogsModal: React.FC<K8sPodLogsModalProps> = ({
    agentId,
    clientId,
    podUid,
    podName,
    initialContainer,
    onClose,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [logsData, setLogsData] = useState<LogsResponse | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedLevel, setSelectedLevel] = useState<string>('all');
    const [selectedContainer, setSelectedContainer] = useState<string>('all');
    const [wrapLines, setWrapLines] = useState<boolean>(true);
    const [copied, setCopied] = useState<boolean>(false);
    const [tailInput, setTailInput] = useState<string>('100');

    const logContainerRef = useRef<HTMLDivElement>(null);

    // Format timestamps nicely
    const formatTime = (tsString: string) => {
        try {
            const d = new Date(tsString);
            if (isNaN(d.getTime())) return tsString;
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const hh = String(d.getHours()).padStart(2, '0');
            const min = String(d.getMinutes()).padStart(2, '0');
            const ss = String(d.getSeconds()).padStart(2, '0');
            const ms = String(d.getMilliseconds()).padStart(3, '0');
            return `${mm}-${dd} ${hh}:${min}:${ss}.${ms}`;
        } catch {
            return tsString;
        }
    };

    // Load logs from API
    const fetchLogs = async (customTail?: number | any) => {
        const activeTail = (typeof customTail === 'number') ? customTail : (parseInt(tailInput, 10) || 100);
        setLoading(true);
        setError(null);
        try {
            const clusterName = new URLSearchParams(window.location.search).get('cluster_name');
            const params: any = {
                agent_id: agentId,
                client_id: clientId,
                pod_uid: podUid,
                tail: activeTail,
            };
            if (clusterName) {
                params.cluster_name = clusterName;
            }

            const res = await baseapi.get('/k8s_dashboard/logs', { params });
            if (res.data?.status) {
                setLogsData(res.data);
                // If an initial container was clicked, default the container filter to it
                if (initialContainer) {
                    setSelectedContainer(initialContainer);
                }
            } else {
                setError(res.data?.message || 'Failed to retrieve logs');
            }
        } catch (err: any) {
            console.error('Error fetching logs:', err);
            setError(err.response?.data?.message || err.message || 'Error occurred while loading logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [agentId, clientId, podUid]);

    // Unique list of containers in the log data
    const containers = useMemo(() => {
        if (!logsData?.data) return [];
        const unique = new Set<string>();
        logsData.data.forEach((log) => {
            if (log.container) unique.add(log.container);
        });
        return Array.from(unique);
    }, [logsData]);

    // Filtered logs list
    const filteredLogs = useMemo(() => {
        if (!logsData?.data) return [];

        return logsData.data.filter((log) => {
            // Filter by container
            if (selectedContainer !== 'all' && log.container !== selectedContainer) {
                return false;
            }

            // Filter by level
            if (selectedLevel !== 'all') {
                const normLevel = (log.level || '').toLowerCase();
                const normSelected = selectedLevel.toLowerCase();
                if (normSelected === 'error') {
                    if (normLevel !== 'error' && normLevel !== 'err') return false;
                } else if (normSelected === 'warning') {
                    if (normLevel !== 'warning' && normLevel !== 'warn') return false;
                } else if (normSelected === 'info') {
                    if (normLevel !== 'info' && normLevel !== 'inf') return false;
                } else if (normSelected === 'other') {
                    if (['error', 'err', 'warning', 'warn', 'info', 'inf'].includes(normLevel)) return false;
                }
            }

            // Filter by search term
            if (searchTerm.trim() !== '') {
                const searchLower = searchTerm.toLowerCase();
                const msgLower = (log.message || '').toLowerCase();
                const containerLower = (log.container || '').toLowerCase();
                const levelLower = (log.level || '').toLowerCase();
                return msgLower.includes(searchLower) || containerLower.includes(searchLower) || levelLower.includes(searchLower);
            }

            return true;
        });
    }, [logsData, selectedContainer, selectedLevel, searchTerm]);



    // Handle copying logs to clipboard
    const handleCopy = async () => {
        if (!filteredLogs.length) return;
        const text = filteredLogs.map((log) => {
            const time = formatTime(log.timestamp);
            const containerInfo = log.container ? `[${log.container}] ` : '';
            const levelInfo = log.level ? `[${log.level.toUpperCase()}] ` : '';
            return `${time} ${containerInfo}${levelInfo}${log.message}`;
        }).join('\n');

        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy logs:', err);
        }
    };

    // Helper to highlight matching text search
    const highlightText = (text: string, search: string) => {
        if (!search.trim()) return text;
        const parts = text.split(new RegExp(`(${search.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi'));
        return (
            <>
                {parts.map((part, i) => 
                    part.toLowerCase() === search.toLowerCase() ? (
                        <mark key={i} className="bg-orange-500/40 text-orange-200 px-0.5 rounded border-b border-orange-500/60">{part}</mark>
                    ) : part
                )}
            </>
        );
    };

    // Helper for level badges colors
    const getLevelColors = (level: string) => {
        const norm = (level || '').toLowerCase();
        if (norm === 'error' || norm === 'err') {
            return 'bg-red-500/10 border border-red-500/30 text-red-400 font-bold';
        }
        if (norm === 'warning' || norm === 'warn') {
            return 'bg-amber-500/10 border border-amber-500/30 text-amber-400 font-bold';
        }
        if (norm === 'info' || norm === 'inf') {
            return 'bg-blue-500/10 border border-blue-500/30 text-blue-400 font-bold';
        }
        return 'bg-gray-800 border border-gray-700 text-gray-300';
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 font-sans" onClick={onClose}>
            <div className="bg-gray-950 border border-gray-700 rounded-xl shadow-2xl w-full max-w-6xl h-[85vh] flex flex-col overflow-hidden text-gray-200" onClick={(e) => e.stopPropagation()}>
                
                {/* Modal Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center px-4 py-3 border-b border-gray-800 bg-gray-900 shrink-0 gap-3">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-orange-500/10 rounded-lg border border-orange-500/20 text-orange-400">
                            <AlignLeft className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-gray-100 font-mono text-sm leading-tight">
                                logs — <span className="font-bold text-white">{podName}</span>
                            </h3>
                            <p className="text-[11px] text-gray-500 font-mono mt-0.5">
                                namespace: {logsData?.namespace || '—'} | total lines: {logsData?.count || 0}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
                        {/* Refresh Button */}
                        <button
                            onClick={fetchLogs}
                            disabled={loading}
                            className="p-2 rounded-lg bg-gray-800/80 border border-gray-700 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all text-gray-300 disabled:opacity-40"
                            title="Refresh Logs"
                        >
                            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                        </button>

                        {/* Copy Button */}
                        <button
                            onClick={handleCopy}
                            disabled={!filteredLogs.length}
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold font-mono bg-gray-800/80 border border-gray-700 rounded-lg text-gray-300 hover:text-orange-400 hover:border-orange-500/50 hover:bg-orange-500/10 transition-all disabled:opacity-40"
                        >
                            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                            {copied ? 'COPIED!' : 'COPY LOGS'}
                        </button>

                        {/* Close button */}
                        <button onClick={onClose} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors ml-1">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Filter and Control Bar */}
                <div className="px-4 py-3 bg-gray-900/60 border-b border-gray-800/80 flex flex-col md:flex-row gap-3 items-center justify-between shrink-0">
                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                        
                        {/* Container Selection Tabs */}
                        {containers.length > 1 && (
                            <div className="flex bg-black/30 border border-gray-800 rounded-lg p-0.5">
                                <button
                                    onClick={() => setSelectedContainer('all')}
                                    className={`px-2.5 py-1 text-[11px] font-mono font-bold rounded-md transition-all ${
                                        selectedContainer === 'all'
                                            ? 'bg-orange-600 text-white shadow-sm'
                                            : 'text-gray-400 hover:text-gray-200'
                                    }`}
                                >
                                    all containers
                                </button>
                                {containers.map((c) => (
                                    <button
                                        key={c}
                                        onClick={() => setSelectedContainer(c)}
                                        className={`px-2.5 py-1 text-[11px] font-mono font-bold rounded-md transition-all ${
                                            selectedContainer === c
                                                ? 'bg-orange-600 text-white shadow-sm'
                                                : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        {c}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Level Filters */}
                        <div className="flex bg-black/30 border border-gray-800 rounded-lg p-0.5">
                            {(['all', 'error', 'warning', 'info', 'other'] as const).map((lvl) => (
                                <button
                                    key={lvl}
                                    onClick={() => setSelectedLevel(lvl)}
                                    className={`px-2.5 py-1 text-[10px] uppercase font-mono font-bold rounded-md transition-all ${
                                        selectedLevel === lvl
                                            ? lvl === 'error'
                                                ? 'bg-red-600 text-white'
                                                : lvl === 'warning'
                                                ? 'bg-amber-600 text-white'
                                                : lvl === 'info'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-orange-600 text-white'
                                            : 'text-gray-400 hover:text-gray-200'
                                    }`}
                                >
                                    {lvl}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 w-full md:w-auto justify-end">
                        {/* Search Input */}
                        <div className="relative w-full sm:w-60">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Filter log entries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-black/40 border border-gray-800 hover:border-gray-700/80 focus:border-orange-500/50 text-gray-200 text-xs rounded-lg pl-8 pr-3 py-1.5 w-full focus:outline-none transition-colors placeholder:text-gray-600 font-mono h-8"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 hover:text-gray-300 font-mono font-bold"
                                >
                                    clear
                                </button>
                            )}
                        </div>

                        {/* Options Menu/Pills */}
                        <div className="flex items-center gap-3 bg-black/20 border border-gray-800 rounded-lg px-3 h-8 text-[11px] font-mono font-semibold shrink-0">
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={wrapLines}
                                    onChange={(e) => setWrapLines(e.target.checked)}
                                    className="accent-orange-500 rounded border-gray-800 bg-black"
                                />
                                <span className="text-gray-400 hover:text-gray-200">Wrap</span>
                            </label>
                            <div className="w-px h-3 bg-gray-800" />
                            <div className="flex items-center gap-1.5 select-none">
                                <span className="text-gray-500">Tail:</span>
                                <input
                                    type="number"
                                    min={1}
                                    max={10000}
                                    value={tailInput}
                                    onChange={(e) => setTailInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const val = parseInt(tailInput, 10) || 100;
                                            fetchLogs(val);
                                        }
                                    }}
                                    onBlur={() => {
                                        const val = parseInt(tailInput, 10) || 100;
                                        fetchLogs(val);
                                    }}
                                    className="bg-transparent border-0 text-orange-400 w-12 focus:outline-none focus:ring-0 text-center font-bold font-mono h-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Log Output Body */}
                <div className="flex-1 bg-black relative min-h-0 flex flex-col">
                    
                    {/* Loading State */}
                    {loading && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm">
                            <RefreshCw className="w-10 h-10 animate-spin text-orange-500 mb-4" />
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse font-mono">
                                Loading pod log stream...
                            </span>
                        </div>
                    )}

                    {/* Error State */}
                    {error && !loading && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/90 backdrop-blur-sm p-8 text-center">
                            <AlertCircle className="w-12 h-12 mb-4 text-red-500" />
                            <h2 className="text-lg font-bold mb-2 text-red-400">Failed to Load Logs</h2>
                            <p className="text-sm text-gray-400 max-w-md mb-6 font-mono">
                                {error}
                            </p>
                            <button
                                onClick={fetchLogs}
                                className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold font-mono rounded-lg transition-colors shadow-lg"
                            >
                                RETRY CONNECTION
                            </button>
                        </div>
                    )}

                    {/* Logs Screen */}
                    <div 
                        ref={logContainerRef}
                        className="flex-1 overflow-y-auto overflow-x-auto p-4 custom-scroll font-mono text-[11px] leading-relaxed select-text"
                    >
                        {!loading && filteredLogs.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center p-8">
                                <FileText className="w-8 h-8 text-gray-600 mb-2" />
                                <p className="text-gray-500 font-bold">No log messages found</p>
                                <p className="text-[10px] text-gray-600 mt-1">
                                    {searchTerm ? 'Try adjusting your search filter' : 'No logs generated by this container yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="min-w-max space-y-0.5">
                                {filteredLogs.map((log, idx) => {
                                    const time = formatTime(log.timestamp);
                                    
                                    return (
                                        <div 
                                            key={idx} 
                                            className="flex items-start hover:bg-white/[0.03] rounded px-1.5 py-0.5 group gap-3 transition-colors border-l-2 border-transparent hover:border-orange-500/20"
                                        >
                                            {/* Line Number / Index */}
                                            <span className="text-[9px] text-gray-200 w-8 select-none text-right font-mono self-start mt-0.5">
                                                {idx + 1}
                                            </span>

                                            {/* Timestamp */}
                                            <span className="text-gray-200 select-none whitespace-nowrap self-start mt-0.5">
                                                {time}
                                            </span>

                                            {/* Container Badge (shown if "all" container filter selected and more than 1 container exists) */}
                                            {selectedContainer === 'all' && log.container && (
                                                <span className="text-orange-400/80 px-1 py-0.5 bg-orange-500/5 border border-orange-500/10 rounded text-[9px] select-none uppercase tracking-wider font-bold whitespace-nowrap self-start">
                                                    {log.container}
                                                </span>
                                            )}

                                            {/* Log Level Badge */}
                                            {log.level && (
                                                <span className={`px-1 py-0.5 rounded text-[9px] select-none uppercase tracking-wider whitespace-nowrap self-start ${getLevelColors(log.level)}`}>
                                                    {log.level}
                                                </span>
                                            )}

                                            {/* Log Message */}
                                            <div className="flex-1 min-w-0">
                                                <pre className={`font-mono text-gray-300 leading-normal ${wrapLines ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'}`}>
                                                    {highlightText(log.message, searchTerm)}
                                                </pre>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>


                </div>
            </div>
        </div>
    );
};
